import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import ReactDOM from 'react-dom';
import Carousel from 'components/Carousel';
import NiceModal from 'components/modals/NiceModal';
import CarouselPictureModal from 'modals/CarouselPictureModal/CarouselPictureModal';
import { isNil } from 'ramda';
import styled, { keyframes } from 'styled-components';

interface MediaItem {
  url: string;
  videoCoverUrl?: string;
  isVideo?: boolean;
  order?: number;
}

interface MediaPackGridModalProps {
  title: string;
  mediaItems: MediaItem[];
  onClose: () => void;
}

const CloseButton = styled.div`
  @media (max-width: 430px) {
    position: fixed;
    right: 24px;
    top: 24px;
    z-index: 1000;
    cursor: pointer;

    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const MediaPackGridModal: React.FC<MediaPackGridModalProps> = (props) => {
  const { title, mediaItems, onClose } = props;
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const sortedMediaItems = [...mediaItems].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

  const formattedMediaItems = sortedMediaItems.map((item) => ({
    ...item,
    isVideo: !!item?.videoCoverUrl,
  }));

  const handleImageLoad = (url: string) => {
    setLoadedImages((prev) => ({ ...prev, [url]: true }));
  };

  const handleImageError = (url: string) => {
    setLoadedImages((prev) => ({ ...prev, [url]: false }));
  };

  const transformMediaItems = (items: MediaItem[]) => {
    return items.map((item, index) => ({
      id: String(index),
      url: item.isVideo ? item.videoCoverUrl || item.url : item.url,
      type: item.isVideo ? ('video' as const) : ('image' as const),
    }));
  };

  return ReactDOM.createPortal(
    <NiceModal
      onClose={onClose}
      closeOnOutsideClick={false}
      customCloseButton={isMobile && isNil(selectedPhotoIndex) ? <CloseButton /> : undefined}
    >
      <ModalContent>
        <TopBar>
          <Title>{title}</Title>
        </TopBar>

        <GridContainer>
          {formattedMediaItems.map((item, index) => (
            <GridItem key={index} onClick={() => setSelectedPhotoIndex(index)}>
              {item.isVideo ? (
                <VideoWrapper>
                  <video
                    src={item.videoCoverUrl}
                    poster={item.url}
                    controls={false}
                    muted
                    loop
                    playsInline
                  />
                  <VideoIndicator />
                </VideoWrapper>
              ) : (
                <>
                  {!loadedImages[item.url] && <SkeletonItem />}
                  <Image
                    src={item.url}
                    alt={`Media item ${index + 1}`}
                    style={{ display: loadedImages[item.url] ? 'block' : 'none' }}
                    onLoad={() => handleImageLoad(item.url)}
                    onError={() => handleImageError(item.url)}
                  />
                </>
              )}
            </GridItem>
          ))}
        </GridContainer>

        {selectedPhotoIndex !== null && (
          <CarouselPictureModal
            onClose={() => setSelectedPhotoIndex(null)}
            closeOnOutsideClick={false}
          >
            <Carousel
              photos={transformMediaItems(formattedMediaItems)}
              initialIndex={selectedPhotoIndex}
            />
          </CarouselPictureModal>
        )}
      </ModalContent>
    </NiceModal>,
    document.body
  );
};

export default MediaPackGridModal;

const ModalContent = styled.div`
  height: 686px;
  min-height: 686px;
  width: 945px;
  min-width: 945px;
  max-width: 945px;
  background: #1A1A1A;
  border-radius: 12px;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 6px; 
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  scrollbar-width: thin;
  scrollbar-color: #333 transparent;

  @media (max-width: 720px) {
    width: 100vw;
    min-width: 100vw;
    max-width: 100vw;
    height: calc(var(--vh, 1vh) * 100);
    min-height: calc(var(--vh, 1vh) * 100);
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;

    padding: 0;
    margin: 0;
    border-radius: 0;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;

  @media (max-width: 720px) {
    margin-bottom: 32px;
    padding: 18px 18px 0;
  }
`;

const Title = styled.h2`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF;
  margin: 0;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 290.58px);
  gap: 16px;
  width: 100%;
  flex-grow: 1;
  overflow-y: auto;
  justify-content: center;
  overflow-x: hidden;

  @media (max-width: 720px) {
    overflow-x: visible;
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 6px));
    gap: 12px;
    width: calc(100% - 36px);
    margin: 0 18px;
    justify-content: space-between;
    overflow-y: visible;
    flex-grow: 1;
    align-content: flex-start;
    padding-bottom: 20px;
  }
`;

const GridItem = styled.div`
  width: 290.58px;
  height: 287px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: #131313;
  position: relative;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 720px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  transition: background 0.2s ease;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 12px 0 12px 20px;
    border-color: transparent transparent transparent white;
    opacity: 0.8;
    transition: opacity 0.2s ease;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonItem = styled.div`
  aspect-ratio: 1;
  border-radius: 12px;
  background: linear-gradient(to right, #1A1A1A 4%, #2A2A2A 25%, #1A1A1A 36%);
  background-size: 1000px 100%;
  animation: ${shimmer} 3.5s infinite linear;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;